import { renderInPortal, usePortal } from "frontend/modal/usePortal";
import { PortalID } from "frontend/modal/portal-types";
import useStateValue from "frontend/state/value";
import { useEffect, useRef, useState } from "react";
import style from "./app-header.module.css";
import "@mondaydotcomorg/monday-ui-components/dist/ProductsSwitcher.css";
import "@mondaydotcomorg/monday-ui-components/dist/main.css";
import "monday-ui-react-core/dist/main.css";
import dynamic from "next/dynamic";
import PaymentNotification from "./payment-notification";
import BillingNotification from "./billing-notification";
import TrialDaysLeftNotification from "./trial-days-left-notification";
import { isBasicPlan, isTrialActive, isTrialExpired } from "shared/util/billing";
import AppLogo from "../ui-components/logo";
import AppHeaderBoard from "./app-header-board";
import { TipName } from "shared/datamodel/schemas";
import InviteNotification from "./invite-notification";
import { useAtomValue, useSetAtom } from "jotai";
import { accountSubscribedCountAtom, userTipsAtom } from "state-atoms";
import { markTipSeen } from "frontend/api";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { isBannerNotificationOnAtom } from "state-atoms/general-atoms";
import { useBoardValue } from "frontend/state/board-state";
import { initButtonText, useRestricted } from "frontend/hooks/use-restricted";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import StyledButton, { ButtonStyle } from "frontend/ui-components/styled-button";
import { InstanceType } from "shared/datamodel/schemas/ability";
import AskToEditToast from "frontend/ui-components/toast/ask-to-edit-toast";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import useAbility from "frontend/hooks/use-ability";
import { CheckoutSource } from "frontend/billing/checkout";
import { getPathPrefix } from "../utils/getPathPrefix";
import ProfileNew from "frontend/user-presence/profile-new";
import { isCIM, useIsCIMReorderEnabled } from "utils/url-utils";
import { SearchSection } from "frontend/boards-grid/search-section/search-section";
import ResendConfirmationEmailNotification from "./resend-confirmation-email-notification/resend-confirmation-email-notification";
import { CimHeader } from "frontend/app-header/cim-header/cim-header";

const ProductsSwitcher = dynamic(() => import("@mondaydotcomorg/monday-ui-components/dist/ProductsSwitcher"), {
  ssr: false,
});

export default function AppHeader({
  isBoardHeader = true,
  showsSwitcher = true,
  title = null,
  renderUpgradeCTA,
  renderBoardUsersPresence,
  canEditBoard,
  children,
  onInviteClick,
  onCreateTemplate,
  onCreateTemplateLink,
  setShowUpgradeModal,
  onRestrictedClick,
  reps,
  setFollow,
}: {
  isBoardHeader: boolean;
  showsSwitcher?: boolean;
  title?: JSX.Element | null;
  renderUpgradeCTA?: () => JSX.Element;
  renderBoardUsersPresence?: any;
  canEditBoard?: boolean | null;
  children?: any;
  onInviteClick?: (title?: string) => void;
  onCreateTemplate?: () => void;
  onCreateTemplateLink?: () => void;
  setShowUpgradeModal?: (reason: CheckoutSource | null) => void;
  onRestrictedClick?: () => void;
  reps?: any;
  setFollow: (id: string) => void;
}) {
  const userTips = useAtomValue(userTipsAtom);

  const [{ user, account, teams }] = useStateValue();
  const boardValue = useBoardValue();

  const [showNotification, setShowNotification] = useState(false);
  const setIsBannerNotificationOn = useSetAtom(isBannerNotificationOnAtom);
  const [showPaymentNotification, setShowPaymentNotification] = useState(false);
  const [showBillingNotification, setShowBillingNotification] = useState(false);
  const [showTrialNotification, setShowTrialNotification] = useState(false);
  const [showResendConfirmationEmailNotification, setShowResendConfirmationEmailNotification] = useState(false);
  const target = usePortal(PortalID.AppHeader);
  const boardUserValue = boardValue?.length ? boardValue[0].user : null;
  const { canPerformAnyAction } = useAbility();
  const { showAskToEditToast, viewerButtonText } = useRestricted();
  const shouldShoWRestrictedButton = user && !canPerformAnyAction(user, teams[0], InstanceType.Team);

  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerHover = useHover(viewerRef);
  const accountSubscribedCount = useAtomValue(accountSubscribedCountAtom);
  const isCimReorderEnabled = useIsCIMReorderEnabled();

  const [showInviteNotification, setShowInviteNotification] = useState<boolean>(false);

  useEffect(() => {
    setShowInviteNotification(!userTips.has(TipName.inviteNotification) && !isCIM());
  }, [userTips]);

  const shouldRenderUpgradeCTA =
    user && isBasicPlan(user?.planInfo?.plan_id) && user?.planInfo?.source !== "touch" && renderUpgradeCTA;

  const isHomePage = !isBoardHeader;

  useEffect(() => {
    const seatsCount = user?.planInfo?.seats_count ?? Number.NaN;

    const shouldShowBillingNotification =
      !!user?.account?.id &&
      !user?.planInfo?.invoice_paid &&
      !isBasicPlan(user?.planInfo?.plan_id) &&
      !user?.planInfo?.trial_end_date &&
      user?.account?.id !== "5" &&
      isHomePage;
    setShowBillingNotification(shouldShowBillingNotification ?? false);

    const shouldShowPaymentNotification =
      !Number.isNaN(seatsCount) &&
      accountSubscribedCount > seatsCount &&
      !isBasicPlan(user?.planInfo?.plan_id) &&
      user?.planInfo?.trial_end_date === null &&
      user?.account?.id !== "5" &&
      isHomePage; //don't show this to monday account
    setShowPaymentNotification(shouldShowPaymentNotification ?? false);

    let showTrial;
    if (user) {
      const trialActive = isTrialActive(user.planInfo ?? null);
      const trialExpired = isTrialExpired(user.planInfo ?? null);
      showTrial = trialActive || trialExpired;
      setShowTrialNotification(showTrial);
      setShowResendConfirmationEmailNotification(user && !user.isVerified);
    }
  }, [user, accountSubscribedCount]);

  useEffect(() => {
    if (
      !boardUserValue?.isAnonymous &&
      (showBillingNotification ||
        showPaymentNotification ||
        showTrialNotification ||
        showInviteNotification ||
        showResendConfirmationEmailNotification)
    ) {
      setShowNotification(true);
      setIsBannerNotificationOn(true);
    } else {
      setShowNotification(false);
      setIsBannerNotificationOn(false);
    }
  }, [
    showBillingNotification,
    showPaymentNotification,
    showTrialNotification,
    showInviteNotification,
    showResendConfirmationEmailNotification,
  ]);

  function renderSwitcher() {
    return (
      account && (
        <ProductsSwitcher
          iconSize={24}
          domain="monday.com"
          protocol="https"
          selectedProductKind={consts.WORKCANVAS_PRODUCT_KIND}
          moveBy={{ secondary: 10, main: 2 }}
          subdomain={account.slug}
        />
      )
    );
  }

  function renderNotificationContent() {
    if (showBillingNotification) {
      // showBillingNotification is noly true if user.account.id exists (See above useEffect)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <BillingNotification accountId={user!.account!.id!} />;
    } else if (showPaymentNotification) {
      // showBillingNotification is noly true if user.planInfo.seats_count exists (See above useEffect)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <PaymentNotification unpaidSeats={accountSubscribedCount - user!.planInfo!.seats_count!} />;
    } else if (user && showTrialNotification) {
      return <TrialDaysLeftNotification user={user} />;
    } else if (showResendConfirmationEmailNotification && user) {
      return <ResendConfirmationEmailNotification email={user.email} />;
    } else if (showInviteNotification) {
      return (
        <InviteNotification
          onDismiss={() => {
            setShowInviteNotification(false);
            markTipSeen(TipName.inviteNotification);
            tracking.trackEvent(
              consts.TRACKING_CATEGORY.ADMIN_ACTION,
              "invite_notification_dismissed",
              "invite_type",
              isBoardHeader ? "canvas" : "account"
            );
          }}
          onInviteClick={onInviteClick}
        />
      );
    }
  }

  const renderRestrictedActions = () => {
    const tooltip = (
      <div style={{ textAlign: "left", fontSize: 12, fontWeight: 300 }}>
        <p>If you want to edit, click to request access</p>
      </div>
    );
    if (shouldShoWRestrictedButton) {
      return (
        <div className={style.restrictedButtons}>
          <StyledButton
            data-testId="restricted-member-button"
            title={viewerButtonText}
            style={ButtonStyle.outline}
            icon={viewerButtonText === initButtonText ? getPathPrefix("/images/blue-eye.svg") : undefined}
            customStyle={{
              height: "30px",
              minWidth: "fit-content",
              fontWeight: "400",
              fontFamily: "Poppins",
              borderRadius: "6px",
              width: 212,
              padding: viewerButtonText === "Viewer" ? "8px 12px 8px 8px" : "unset",
            }}
            ref={viewerRef}
            iconStyle={{ width: 20, height: 20 }}
            onClick={() => onRestrictedClick && onRestrictedClick()}
          />
          {viewerHover && (
            <Tooltip label={tooltip} relativeTo={viewerRef} side={"bottom"} customStyle={{ borderRadius: 3 }} />
          )}
        </div>
      );
    } else {
      return null;
    }
  };

  function renderContent() {
    return (
      <div className={style.container} id="app-header-container">
        <div className={style.header}>
          <div data-testid="topleftlogo">
            <AppLogo source="app-header" />
          </div>
          {renderRestrictedActions()}
          {!isBoardHeader && <SearchSection />}
          {isBoardHeader && onCreateTemplate && onCreateTemplateLink && setShowUpgradeModal ? (
            <AppHeaderBoard
              title={title}
              renderUpgradeCTA={renderUpgradeCTA}
              renderBoardUsersPresence={renderBoardUsersPresence}
              onInviteClick={onInviteClick}
              onCreateTemplate={onCreateTemplate}
              onCreateTemplateLink={onCreateTemplateLink}
              setShowUpgradeModal={setShowUpgradeModal}
              canEditBoard={canEditBoard}
            >
              {children}
            </AppHeaderBoard>
          ) : (
            <div className={style.right}>
              <div className={style.content}>{children}</div>
              {shouldRenderUpgradeCTA && renderUpgradeCTA()}
              <ProfileNew isBoardHeader={isBoardHeader} />
              {showsSwitcher && renderSwitcher()}
            </div>
          )}
        </div>
        {showNotification && <div className={style.notification}>{renderNotificationContent()}</div>}
        {showAskToEditToast && <AskToEditToast />}
      </div>
    );
  }

  if (isBoardHeader && isCimReorderEnabled) {
    return <CimHeader reps={reps} setFollow={setFollow} />;
  }

  return renderInPortal(target, renderContent);
}
